import React, { useState } from 'react'
import { ChevronRight } from 'lucide-react'
import type { UserService } from '@duck/api-definitions'
import { Checkbox, Skeleton, cn } from '@duck/ui'
import { useTar, type TranslationKey } from '@/modules/i18n'
import { GradeBadge } from '@/components/GradeBadge/GradeBadge'
import {
  getPersonStatusColorClass,
  getDisplayedStatusDate,
  formatShortNumericDate,
} from '@/hooks/TableSettings/peopleTable.utils'
import { useCopyInviteLink } from '@/hooks/useCopyInviteLink'
import { PersonAssignmentsRoster } from './PersonAssignmentsRoster'
import { PersonDetailsPanel } from './PersonDetailsPanel'
import type { ResultsDetails } from '../assignments/SubmissionResultsDialog'
import type { PersonRowModel } from '../cohortV2Data'

interface PersonRowProps {
  model: PersonRowModel
  // Identity is joined from the directory progressively; while it's still loading an unresolved
  // person renders a skeleton rather than its raw id.
  directoryLoading: boolean
  checked: boolean
  onToggle: (checked: boolean) => void
  getAssignmentTaskName: (assignmentId: string) => string
  isTaskLoading: boolean
  onViewResults: (d: ResultsDetails) => void
}

// Status word + its date: real join date (user.createdAt) for joined members, else the person
// proxy (createdAt for added, updatedAt for invited). Date shows only when it exists.
const StatusLabel: React.FC<{ person: UserService.PersonDirectoryEntry; joinedAt?: string }> = ({ person, joinedAt }) => {
  const { t } = useTar()
  const word = t(`partner.onboarding_${person.onboardingStatus}` as TranslationKey)
  const date = formatShortNumericDate(getDisplayedStatusDate(person, joinedAt))
  return (
    <span className="text-xs">
      <span className={cn('font-medium', getPersonStatusColorClass(person.onboardingStatus))}>{word}</span>
      {date && (
        <>
          <span className="text-muted-foreground">: </span>
          <span className="text-muted-foreground" dir="ltr">{date}</span>
        </>
      )}
    </span>
  )
}

export const PersonRow: React.FC<PersonRowProps> = ({
  model, directoryLoading, checked, onToggle, getAssignmentTaskName, isTaskLoading, onViewResults,
}) => {
  const { t } = useTar()
  const { person, personId, totalAssignments, submittedAssignments, bestGrade, details, joinedAt } = model
  const [open, setOpen] = useState(false)
  const { copyInviteLink, copyingPersonId } = useCopyInviteLink()
  const pct = totalAssignments > 0 ? (submittedAssignments / totalAssignments) * 100 : 0
  const toggleOpen = () => setOpen((v) => !v)

  // Identity fills in from the directory progressively. Until the entry resolves, render a
  // lightweight, non-expandable row (skeleton while loading, raw id as a last resort) — board data
  // (counts/grade) is always present, so the row still carries its metrics.
  if (person === undefined) {
    return (
      <div className="grid min-h-[60px] grid-cols-[26px_auto_1fr_auto] items-center gap-3 border-b border-border/60 px-3.5 last:border-b-0 lg:h-[60px] lg:grid-cols-[26px_auto_minmax(0,1.5fr)_180px_minmax(0,1fr)]">
        <span className="flex items-center">
          <Checkbox aria-label={t('partner.selectPerson', { name: personId })} checked={checked} onCheckedChange={(c) => onToggle(!!c)} className="h-4 w-4" />
        </span>
        <span className="w-3.5 shrink-0" />
        <div className="flex min-w-0 items-center gap-2.5">
          <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary/10 text-xs font-bold text-primary" />
          <div className="flex min-w-0 flex-col">
            {directoryLoading ? <Skeleton className="h-4 w-32" /> : <span className="truncate text-sm font-semibold text-foreground">{personId}</span>}
          </div>
        </div>
        <div className="hidden lg:block">{directoryLoading && <Skeleton className="h-4 w-24" />}</div>
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <span aria-hidden="true" className="hidden h-1.5 w-14 overflow-hidden rounded-full bg-muted lg:block">
            <span className="block h-full bg-primary" style={{ width: `${pct}%` }} />
          </span>
          <span className="hidden lg:inline">{submittedAssignments}/{totalAssignments}</span>
          {bestGrade !== undefined ? <GradeBadge grade={bestGrade} small /> : <span className="hidden text-muted-foreground/70 lg:inline">{t('partner.noGrade')}</span>}
        </div>
      </div>
    )
  }

  const name = `${person.firstName || ''} ${person.lastName || ''}`.trim() || person.email
  const initials = `${person.firstName?.charAt(0) || ''}${person.lastName?.charAt(0) || ''}`.toUpperCase()
  const copying = copyingPersonId === personId

  return (
    <div className="border-b border-border/60 last:border-b-0">
      <div
        role="button"
        tabIndex={0}
        aria-expanded={open}
        onClick={toggleOpen}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggleOpen() } }}
        className="grid min-h-[60px] cursor-pointer grid-cols-[26px_auto_1fr_auto] items-center gap-3 px-3.5 hover:bg-muted/30 lg:h-[60px] lg:grid-cols-[26px_auto_minmax(0,1.5fr)_180px_minmax(0,1fr)]"
      >
        <span onClick={(e) => e.stopPropagation()} className="flex items-center">
          <Checkbox aria-label={t('partner.selectPerson', { name })} checked={checked} onCheckedChange={(c) => onToggle(!!c)} className="h-4 w-4" />
        </span>
        <ChevronRight className={cn('h-3.5 w-3.5 shrink-0 text-muted-foreground transition-transform', open ? 'rotate-90' : 'rtl:rotate-180')} />
        <div className="flex min-w-0 items-center gap-2.5">
          <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary/10 text-xs font-bold text-primary">{initials}</span>
          <div className="flex min-w-0 flex-col">
            <span className="truncate text-sm font-semibold text-foreground">{name}</span>
          </div>
        </div>
        <div className="hidden items-center gap-2 lg:flex">
          <StatusLabel person={person} joinedAt={joinedAt} />
          {/* Invite tokens are fetched on demand (not shipped with the directory), so the link is
              resolved only when the partner clicks. Shown for people who haven't joined yet. */}
          {person.onboardingStatus === 'invited' && (
            <button
              type="button"
              disabled={copying}
              onClick={(e) => { e.stopPropagation(); copyInviteLink(personId) }}
              className="text-xs text-primary hover:underline disabled:opacity-50"
            >
              {t('common.copyLink')}
            </button>
          )}
        </div>
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <span aria-hidden="true" className="hidden h-1.5 w-14 overflow-hidden rounded-full bg-muted lg:block">
            <span className="block h-full bg-primary" style={{ width: `${pct}%` }} />
          </span>
          <span className="hidden lg:inline">{submittedAssignments}/{totalAssignments}</span>
          {bestGrade !== undefined ? (
            <GradeBadge grade={bestGrade} small />
          ) : (
            <>
              {/* Desktop keeps a plain placeholder — the dedicated Status column already carries the label. */}
              <span className="hidden text-muted-foreground/70 lg:inline">{t('partner.noGrade')}</span>
              {/* Mobile has no Status column, so an ungraded row surfaces the status label here instead. */}
              <span className="lg:hidden"><StatusLabel person={person} joinedAt={joinedAt} /></span>
            </>
          )}
        </div>
      </div>
      {open && (
        // The expanded body reads as a single recessed surface folded out of the row: inset
        // shadows sink it below the header/next row, grouping bio + roster as one unit.
        <div className="bg-background/40 shadow-recess">
          <PersonDetailsPanel person={person} joinedAt={joinedAt} />
          <PersonAssignmentsRoster
            person={person}
            details={details}
            getAssignmentTaskName={getAssignmentTaskName}
            isTaskLoading={isTaskLoading}
            onViewResults={onViewResults}
          />
        </div>
      )}
    </div>
  )
}
